import { array, boolean, number, object, record, string, z, ZodType } from 'zod';

import { uuidv4 } from './uuid';
import {
  AdSlotConstraintConstraintDto,
  AdSlotConstraintTypeEnum,
  AdSlotFormatEnum,
  AdSlotPositionInjectTypeEnum,
  AdSlotSettingLinkPropertiesDto,
  LinkingTypeEnum,
  LinkPropertiesWithHref,
  NpiDestinationEnum,
  NpiDto,
  NpiSourceEnum,
  PublicAdSetDto,
  PublicAdSlotDto,
  PublicRecommendationItemDto,
  PublicSettingsDto,
  SwoopIntegrationTypeEnum,
  CreativeTypeEnum,
  IABFormatEnum,
} from '../dto/recs.dto';

const cssSchema = object({
  fontSize: string().optional().nullable(),
}).passthrough().optional();

const linkPropertiesSchema = (object({
  target: string().optional().nullable(),
  rel: string().optional().nullable(),
  referrerPolicy: string().optional().nullable(),
}) satisfies ZodType<LinkPropertiesWithHref>).optional().nullable();

const linkPropertiesWithHrefSchema = (object({
  target: string().optional().nullable(),
  rel: string().optional().nullable(),
  referrerPolicy: string().optional().nullable(),
  href: string().optional().nullable(),
}) satisfies ZodType<LinkPropertiesWithHref>).optional().nullable();

export const adSlotSchema = object({
  identifier: string().uuid(),
  creativeType: z.nativeEnum(CreativeTypeEnum),
  settings: object({
    positionSelector: string().optional().nullable(),
    positionInjectType: z.nativeEnum(AdSlotPositionInjectTypeEnum),
    maxNumberOfColumns: number().optional().nullable(),
    footerType: string().optional().nullable(),
    format: z.nativeEnum(AdSlotFormatEnum),
    datasetId: string().optional().nullable(),
    linkCloaking: boolean(),
    headingText: string().optional().nullable(),
    privacyPolicyUrl: string().optional().nullable(),
    linkProperties: (object({
      internal: linkPropertiesSchema,
      external: linkPropertiesSchema,
      subtitle: linkPropertiesSchema,
      privacyPolicy: linkPropertiesWithHrefSchema,
      analytics: linkPropertiesWithHrefSchema,
      brand: linkPropertiesWithHrefSchema,
    }) satisfies ZodType<AdSlotSettingLinkPropertiesDto>).optional().nullable(),
    style: object({
      heading: cssSchema,
      container: cssSchema,
      outerContainer: cssSchema,
      list: cssSchema,
      item: cssSchema,
      itemLink: cssSchema,
      itemTitle: cssSchema,
      itemSubtitle: cssSchema,
      itemMeta: cssSchema,
      itemAuthors: cssSchema,
      footer: cssSchema,
      footerLeft: cssSchema,
      footerRight: cssSchema,
    }).passthrough().optional().nullable(),
  }),
  constraints: array(object({
    type: z.nativeEnum(AdSlotConstraintTypeEnum),
    constraint: object({
      any: boolean(),
      values: array(string()).optional().nullable(),
    }) satisfies ZodType<AdSlotConstraintConstraintDto>,
  })),
}) satisfies ZodType<PublicAdSlotDto>;

export const adSetSchema = object({
  identifier: string().uuid(),
  settings: object({
    swoopId: string().optional().nullable(),
    swoopIntegrationType: z.nativeEnum(SwoopIntegrationTypeEnum).optional().nullable(),
    swoopScriptUrl: string().optional().nullable(),
    npiSources: array(z.nativeEnum(NpiSourceEnum)).nullable(),
    npiDetectionEnabled: boolean().nullable().default(false),
    npiDetectors: object({
      [ NpiSourceEnum.LHK ]: object({ url: string().url() }).optional(),
      [ NpiSourceEnum.DMD ]: object({ url: string().url() }).optional(),
    }).optional().nullable(),
    userMetricsScriptUrl: string().optional().nullable(),
  }),
}) satisfies ZodType<PublicAdSetDto>;

export const publicSettingsSchema = object({
  adSet: adSetSchema,
  adSlots: array(adSlotSchema),
}) satisfies ZodType<PublicSettingsDto>;

export const npiSchema =
  object({
    number: number().optional(),
    encryptedNumber: string().optional(),
    source: z.nativeEnum(NpiSourceEnum),
    destination: z.nativeEnum(NpiDestinationEnum),
  }) satisfies ZodType<NpiDto>;

export const publicNpiListSchema = array(npiSchema) satisfies ZodType<NpiDto[]>;

export const publicRecommendationItemSchema = object({
  id: string().uuid(),
  creativeType: z.nativeEnum(CreativeTypeEnum),
  title: string(),
  subtitle: z.union([
    string().optional().nullable(),
    object({
      type: string().optional(),
      content: string().optional(),
      url: string().url().optional(),
    }),
  ]).optional().nullable(),
  url: string().url(),
  campaignId: string(),
  publicationId: string().nullable().optional(),
  linkingType: z.nativeEnum(LinkingTypeEnum),
  clickUrl: string().url(),
  impressionUrl: string().url(),
  authors: string().optional().nullable(),
  publicationName: string().optional().nullable(),
  publicationDate: string().optional().nullable(),
  publicationAbbreviatedName: string().optional().nullable(),
  creative: object({
    type: z.nativeEnum(CreativeTypeEnum),
    format: z.nativeEnum(IABFormatEnum),
    fileResource: object({
      url: string().url(),
    }),
    metadata: object({}).passthrough().nullable().optional(),
  }).optional().nullable(),
}) satisfies ZodType<PublicRecommendationItemDto>;

export const publicRecommendationItemListSchema =
  array(publicRecommendationItemSchema) satisfies ZodType<PublicRecommendationItemDto[]>;

export const externalConfigSchema = object({
  id: string(),
  scriptId: string(),
  datasetId: string().optional(),
  cookie: object({
    expires: number().optional(),
    flags: string().optional(),
    path: string().optional(),
    domain: string().optional(),
  }).optional(),
  userProperties: record(string()).optional(),
  startTime: number().gte(0).optional().default(new Date().getTime()),
  debug: boolean().optional().default(false),
  publicSettings: publicSettingsSchema.optional().nullable(),
});

// Settings that can be set by the page where the application is installed
export const applicationConfigSchema = object({
  widgetInstallUrl: string().nullable().default(null),
  apiUrl: string(),
  version: string(),
  sessionId: string().optional().default(uuidv4()),
}).extend(externalConfigSchema.shape);
